import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';

import { 
  WalletInfo, 
  WalletDashboard, 
  WalletListItem 
} from '@model/wallet';
import { ApiService } from '@theme/utils/api.service';
import { NoInternetHelper } from '@theme/utils/no-internet-helper';

@Injectable({
  providedIn: 'root'
})
export class WalletService {
  private namespace = "customer/wallet";
  private url = "private/agency/customer/wallet";

  constructor(private api: ApiService) {}

  /**
   * Récupérer le wallet complet d'un client avec toutes les informations
   * @param customerUuid UUID du client
   */
  getWallet(customerUuid: string): Observable<WalletInfo> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._get(`${this.url}/${customerUuid}`).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Recalculer manuellement le wallet d'un client
   * @param customerUuid UUID du client
   */
  recalculateWallet(customerUuid: string): Observable<any> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._post(`${this.url}/${customerUuid}/recalculate`, {}).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Récupérer la liste des wallets avec filtres optionnels
   * @param filters Filtres optionnels (statut, enRetard)
   */
  getWalletsList(filters?: {
    statut?: 'A_JOUR' | 'EN_RETARD' | 'DEBITEUR' | 'CREDITEUR';
    enRetard?: boolean;
  }): Observable<WalletListItem[]> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._get(`${this.url}/`, {
      statut: filters?.statut,
      enRetard: filters?.enRetard
    }).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Récupérer le dashboard global des wallets de l'agence
   */
  getDashboard(): Observable<WalletDashboard> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._get(`${this.url}/dashboard`).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Formater le montant en FCFA
   * @param montant Montant à formater
   */
  formatMontant(montant: number): string {
    // Gérer les valeurs null, undefined ou NaN
    if (montant === null || montant === undefined || isNaN(montant)) {
      montant = 0;
    }
    
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant) + ' FCFA';
  }

  /**
   * Obtenir la classe CSS pour le badge de statut
   * @param statut Statut du client
   */
  getStatutBadgeClass(statut: string): string {
    switch (statut) {
      case 'A_JOUR':
        return 'badge-success';
      case 'CREDITEUR':
        return 'badge-info';
      case 'DEBITEUR':
        return 'badge-warning';
      case 'EN_RETARD':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  /**
   * Obtenir la classe CSS pour le niveau de risque
   * @param niveau Niveau de risque
   */
  getRisqueClass(niveau: string): string {
    switch (niveau) {
      case 'FAIBLE':
        return 'text-success';
      case 'MOYEN':
        return 'text-warning';
      case 'ELEVE':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  }

  /**
   * Obtenir l'icône pour le statut du client
   * @param statut Statut du client
   */
  getStatutIcon(statut: string): string {
    switch (statut) {
      case 'A_JOUR':
        return 'fa-check-circle';
      case 'CREDITEUR':
        return 'fa-plus-circle';
      case 'DEBITEUR':
        return 'fa-minus-circle';
      case 'EN_RETARD':
        return 'fa-exclamation-triangle';
      default:
        return 'fa-question-circle';
    }
  }

  /**
   * Traduire le statut en français
   * @param statut Statut à traduire
   */
  translateStatut(statut: string): string {
    const traductions = {
      'A_JOUR': 'À jour',
      'CREDITEUR': 'Créditeur',
      'DEBITEUR': 'Débiteur',
      'EN_RETARD': 'En retard'
    };
    return traductions[statut] || statut;
  }

  /**
   * Traduire le niveau de risque en français
   * @param niveau Niveau à traduire
   */
  translateNiveauRisque(niveau: string): string {
    const traductions = {
      'FAIBLE': 'Faible',
      'MOYEN': 'Moyen',
      'ELEVE': 'Élevé'
    };
    return traductions[niveau] || niveau;
  }

  /**
   * Effectuer un dépôt ou retrait sur le wallet d'un client
   * @param customerUuid UUID du client
   * @param type Type de transaction: 'DEPOT' ou 'RETRAIT'
   * @param montant Montant de la transaction
   * @param libelle Libellé optionnel de la transaction
   */
  transaction(
    customerUuid: string,
    type: 'DEPOT' | 'RETRAIT',
    montant: number,
    libelle?: string
  ): Observable<any> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    const data = {
      customer: customerUuid,
      type: type,
      montant: montant,
      libelle: libelle || null
    };

    return this.api._post(`${this.url}/transaction`, data).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Récupérer la liste des transactions wallet d'un client
   * @param customerUuid UUID du client
   */
  getTransactionsList(customerUuid: string): Observable<any[]> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._get(`${this.url}/${customerUuid}/transactions`).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Valider une transaction wallet
   * @param transactionUuid UUID de la transaction
   */
  validateTransaction(transactionUuid: string): Observable<any> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._post(`${this.url}/transaction/${transactionUuid}/validate`, {}).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Annuler une transaction wallet
   * @param transactionUuid UUID de la transaction
   */
  cancelTransaction(transactionUuid: string): Observable<any> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._post(`${this.url}/transaction/${transactionUuid}/cancel`, {}).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Modifier une transaction wallet
   * @param transactionUuid UUID de la transaction
   * @param data Données de modification (montant, libelle)
   */
  updateTransaction(transactionUuid: string, data: { montant?: number; libelle?: string }): Observable<any> {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return Observable.create(obs => {
        obs.next();
        obs.complete();
      });
    }

    return this.api._post(`${this.url}/transaction/${transactionUuid}/update`, data).pipe(
      map((response: any) => response.data),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Imprimer le reçu d'une transaction wallet
   * @param transactionUuid UUID de la transaction
   * @param agencyKey UUID de l'agence
   * @param userKey UUID de l'utilisateur
   */
  printReceipt(transactionUuid: string, agencyKey: string, userKey: string): void {
    if (!navigator.onLine) {
      NoInternetHelper.internet();
      return;
    }

    const url = `printer/agency/wallet-transaction/${agencyKey}/${userKey}/${transactionUuid}`;
    window.open(`${environment.publicUrl}/${url}`, '_blank');
  }
}